import { ShieldCheck } from 'lucide-react';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
// PROPUESTA AL KIT (DEC-RBAC-045): el kit aún NO exporta un Switch. Se reusa el Switch local (Radix) que
// ya consumen SecurityPolicyEditor y DefaultsStep, mismo precedente. Pendiente de OK de Andres para
// promoverlo a @fiado/react-kit y migrar estos usos.
import { Switch } from '../../../components/ui/switch';
import type { MfaMethodLocal } from '../../../api/types/TenantLocal';
import type { PrincipalIdentifierLocal } from '../../../api/types/PlatformLocal';
// DEC-029: catálogo de métodos + regla anti-lockout (DEC-RBAC-087) centralizados en lib/securityPolicy,
// consumidos por igual por este drill-down, el editor del propio tenant y la política de la plataforma.
import { MFA_METHODS, hasUniversalMethod, principalOtpLabel } from '../../../lib/securityPolicy';
import type { SecurityDraft } from '../../../lib/securityPolicy';

interface Props {
    value: SecurityDraft;
    onChange: (next: SecurityDraft) => void;
    disabled?: boolean;
    /** Identificador principal del tenant (para el invariante anti-lockout). Ausente ⇒ solo exige ≥1. */
    principalIdentifier?: PrincipalIdentifierLocal;
}

/**
 * Verificación en dos pasos (on/off) y métodos permitidos. Controlada; la confirmación de apagarla y el
 * submit los maneja el contenedor.
 */
export function EditTenantSecuritySection({
    value,
    onChange,
    disabled,
    principalIdentifier,
}: Props) {
    // DEC-RBAC-087: el único OTP no puede ser el del identificador secundario. Aviso específico cuando
    // hay métodos elegidos pero ninguno universal (TOTP o el del principal).
    const missingUniversal =
        value.mfaEnabled &&
        value.methodsAllowed.length > 0 &&
        principalIdentifier !== undefined &&
        !hasUniversalMethod(value.methodsAllowed, principalIdentifier);
    const otpLabel = principalIdentifier ? principalOtpLabel(principalIdentifier) : 'correo';
    const patch = (p: Partial<SecurityDraft>): void => {
        onChange({ ...value, ...p });
    };
    const toggleMethod = (method: MfaMethodLocal, checked: boolean): void => {
        patch({
            methodsAllowed: checked
                ? [...new Set([...value.methodsAllowed, method])]
                : value.methodsAllowed.filter((m) => m !== method),
        });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 rounded-md border p-4">
                <div className="space-y-0.5">
                    <Label htmlFor="sec-mfa-enabled" className="cursor-pointer font-medium">
                        Verificación en dos pasos
                    </Label>
                    <p className="text-xs text-muted-foreground">
                        Si la apagas, los usuarios inician sesión solo con contraseña, sin segundo
                        factor. Te recomendamos mantenerla activa.
                    </p>
                </div>
                <Switch
                    id="sec-mfa-enabled"
                    checked={value.mfaEnabled}
                    disabled={disabled}
                    onCheckedChange={(checked) => {
                        patch({ mfaEnabled: checked });
                    }}
                    aria-label="Verificación en dos pasos"
                />
            </div>

            {/* Con la verificación apagada no se muestran los métodos: no aplican. */}
            {value.mfaEnabled && (
                <>
                    <fieldset className="space-y-4" disabled={disabled} aria-disabled={disabled}>
                        <legend className="mb-1 text-sm font-medium">
                            Métodos de verificación permitidos
                        </legend>
                        {MFA_METHODS.map((m) => (
                            <div key={m.value} className="flex items-start gap-3">
                                <Checkbox
                                    id={`sec-mfa-${m.value}`}
                                    checked={value.methodsAllowed.includes(m.value)}
                                    disabled={disabled}
                                    onCheckedChange={(checked) => {
                                        toggleMethod(m.value, checked === true);
                                    }}
                                    className="mt-0.5"
                                />
                                <div className="space-y-0.5">
                                    <Label
                                        htmlFor={`sec-mfa-${m.value}`}
                                        className="cursor-pointer"
                                    >
                                        {m.label}
                                    </Label>
                                    <p className="text-xs text-muted-foreground">{m.hint}</p>
                                </div>
                            </div>
                        ))}
                    </fieldset>

                    <p className="flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                        <ShieldCheck className="mt-0.5 size-4 shrink-0 opacity-70" />
                        <span>
                            El correo electrónico queda siempre disponible como respaldo en casos
                            extremos (recuperación de cuenta), aunque lo desactives aquí.
                        </span>
                    </p>

                    {value.methodsAllowed.length === 0 && (
                        <p className="text-xs text-destructive">
                            Tienes que permitir al menos un método.
                        </p>
                    )}
                    {missingUniversal && (
                        <p className="text-xs text-destructive">
                            El método del identificador secundario no puede ser el único: no todos
                            los usuarios lo tienen. Permite al menos un método universal — la{' '}
                            <strong>app de autenticación</strong> o el código por{' '}
                            <strong>{otpLabel}</strong> (el del identificador principal).
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
